import { useSubscriptionStatus } from '@/features/settings/hooks/use-subscription';

type QuotaType = 'students' | 'aiScans' | 'aiHelps' | 'tasks' | 'subjects';

interface QuotaCheckResult {
  allowed: boolean;
  used: number;
  max: number | null;
  remaining: number | null;
  isNearLimit: boolean;
  isPremium: boolean;
}

export function useQuotaCheck(type: QuotaType): QuotaCheckResult {
  const { data: status } = useSubscriptionStatus();

  if (!status) {
    return { allowed: true, used: 0, max: null, remaining: null, isNearLimit: false, isPremium: false };
  }

  const { planLimits, usageQuota, studentsCount } = status;
  const isPremium = status.subscription?.planTier !== 'free' && !!status.subscription?.planTier;

  const quotaMap: Record<QuotaType, { used: number; max: number | null }> = {
    students: { used: studentsCount, max: planLimits.maxStudents },
    aiScans: { used: usageQuota?.aiScansUsed ?? 0, max: planLimits.aiScansPerMonth },
    aiHelps: { used: usageQuota?.aiHelpsUsed ?? 0, max: planLimits.aiHelpsPerMonth },
    tasks: { used: usageQuota?.tasksCreated ?? 0, max: planLimits.maxTasks },
    subjects: { used: usageQuota?.subjectsCreated ?? 0, max: planLimits.maxSubjects },
  };

  const { used, max } = quotaMap[type];
  const remaining = max === null ? null : Math.max(0, max - used);
  const allowed = max === null || used < max;
  const isNearLimit = max !== null && used >= max * 0.8;

  return { allowed, used, max, remaining, isNearLimit, isPremium };
}
